import React, { useState, useMemo } from "react";
import { Button, Divider, Form, Input, Radio, Spin, Tag } from "antd";
import {
  EntityStatus,
  ServiceActions,
  ServiceDeploymentEnum,
  ServiceTypes,
  tailItemLayout,
} from "../../Constants/Constants";
import { connect } from "react-redux";
import { onFormSubmit } from "../../ApiHandlers/ServiceApiHandlers";
import { withRouter } from "react-router-dom";
import { DiscardWarning } from "../Common/DiscardWarning";
import { BreadCrumbs } from "../Common/BreadCrumbs";

const getInitialValues = (record) => {
  const initialValues = {};
  if (record) {
    initialValues.name = record.name;
    initialValues.dispName = record.dispName;
    initialValues.type = record.type;
    initialValues.deployment = record.deployment;
    initialValues.status = record.status;
  } else {
    initialValues.type = ServiceTypes.core.value;
    initialValues.deployment = ServiceDeploymentEnum.cloud.value;
    initialValues.status = EntityStatus.ACTIVE;
  }
  return initialValues;
};

const getTitle = (action) => {
  switch (action) {
    case ServiceActions.add:
      return "Add";
    case ServiceActions.edit:
      return "Update";
    case ServiceActions.view:
      return "View";
    default:
      return "";
  }
};
const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 12 },
  },
};

export const ServiceForm = (props) => {
  const { onActionClick, record, action } = props;
  const [initialValues, setInitialValues] = useState(getInitialValues(record));
  const title = useMemo(() => getTitle(action), [action]);
  const [form] = Form.useForm();
  const [processing, setProcessing] = useState(false);
  const isViewForm = action === ServiceActions.view;

  const onBackClick = () => {
    const isChanged =
      action === ServiceActions.view ? false : checkForInputChanges();
    DiscardWarning({ isChanged, onActionClick })();
  };

  const checkForInputChanges = () => {
    const fieldsToCheck = [
      "status",
      "deployment",
      "dispName",
      "name",
      "service-form",
      "type",
    ];
    const values = form.getFieldsValue();
    let isInputsChanged = false;
    fieldsToCheck.every((f) => {
      if (
        values.hasOwnProperty(f) &&
        values[f] !== initialValues[f] &&
        (!!values[f] || !!initialValues[f])
      ) {
        isInputsChanged = true;
        return false;
      }
      return true;
    });
    return isInputsChanged;
  };

  return (
    <div className="list-container">
      <div className="page-header-container">
        <BreadCrumbs
          headers={props.parentBread.concat(`${title} Service`)}
          onParentActionClick={onBackClick}
        />
      </div>
      <Divider className="divider" />
      <div className="form-data-container">
        <fieldset disabled={isViewForm}>
          <Form
            form={form}
            name="service-form"
            layout="horizontal"
            initialValues={initialValues}
            onFinish={onFormSubmit({ props, setProcessing, setInitialValues })}
            requiredMark={"optional"}
            size={"middle"}
            {...formItemLayout}
          >
            <Spin spinning={processing} tip={"Processing, please wait..."}>
              <Form.Item
                hasFeedback
                name="name"
                label="Name"
                wrapperCol={{ span: 6 }}
                rules={[
                  { required: true, message: "Please fill in this field" },
                ]}
              >
                <Input
                  title="Name should be unique"
                  disabled={props.action !== ServiceActions.add}
                />
              </Form.Item>

              <Form.Item
                hasFeedback
                name="dispName"
                label="Display Name"
                wrapperCol={{ span: 6 }}
              >
                <Input />
              </Form.Item>

              <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                <Radio.Group
                  options={Object.values(ServiceTypes)}
                  value={initialValues.type}
                />
              </Form.Item>

              <Form.Item
                name="deployment"
                label="Deployment"
                rules={[{ required: true }]}
              >
                <Radio.Group
                  options={Object.values(ServiceDeploymentEnum)}
                  value={initialValues.deployment}
                />
              </Form.Item>

              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true }]}
              >
                <Tag
                  color={
                    initialValues.status === EntityStatus.ACTIVE
                      ? "green"
                      : "red"
                  }
                >
                  {initialValues.status}
                </Tag>
              </Form.Item>

              {!isViewForm && (
                <Form.Item {...tailItemLayout}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="submit-button"
                  >
                    {title}
                  </Button>
                  <Button
                    htmlType="button"
                    onClick={() => form.resetFields()}
                    className="cancel-button"
                  >
                    Cancel
                  </Button>
                </Form.Item>
              )}
            </Spin>
          </Form>
        </fieldset>
      </div>
    </div>
  );
};
const mapStateToProps = (state) => ({
  serviceApi: state.apis.serviceApi,
});

const connectedServiceForm = connect(mapStateToProps)(ServiceForm);

export default withRouter(connectedServiceForm);
